// src/db.js
// Todas as operações de leitura e escrita no Firestore
// Cada função tem uma responsabilidade clara e isolada

import {
  collection, doc, getDoc, getDocs, setDoc, addDoc,
  updateDoc, deleteDoc, onSnapshot, serverTimestamp, query, orderBy, arrayUnion, where, Timestamp, increment,
  writeBatch, deleteField
} from 'firebase/firestore'
import { db } from './firebase.js'

// ═══════════════════════════════════════════════════════════════════════════
// GRUPOS (templates criados pela professora)
// ═══════════════════════════════════════════════════════════════════════════

// Lista todos os grupos disponíveis
export function listenToGroups(callback) {
  const q = query(collection(db, 'groups'), orderBy('createdAt', 'desc'))
  return onSnapshot(q, (snap) => {
    const groups = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
    callback(groups)
  })
}

// Cria um novo grupo (apenas professora)
export async function createGroup({ name, topic, classTime, philIds, createdBy }) {
  const ref = doc(collection(db, 'groups'))
  await setDoc(ref, {
    name,
    topic,
    classTime,       // "HH:MM"
    philIds,         // string[]
    createdBy,       // uid da professora
    teacherMessage:  null,   // preenchido às classTime pela Cloud Function
    teacherMessageTs: null,
    createdAt: serverTimestamp(),
  })
  return ref.id
}

// Busca um grupo pelo id
export async function getGroup(groupId) {
  const snap = await getDoc(doc(db, 'groups', groupId))
  return snap.exists() ? { id: snap.id, ...snap.data() } : null
}

// Escuta mudanças em tempo real num grupo (ex: mensagem da professora chegando)
export function listenToGroup(groupId, callback) {
  return onSnapshot(doc(db, 'groups', groupId), (snap) => {
    if (snap.exists()) callback({ id: snap.id, ...snap.data() })
  })
}

// Atualiza campos de um grupo existente
export async function updateGroup(groupId, data) {
  const ref = doc(db, 'groups', groupId)
  await updateDoc(ref, data)
}

// Exclui um grupo (apenas teacher)
export async function deleteGroup(groupId) {
  const ref = doc(db, 'groups', groupId)
  await deleteDoc(ref)
}

// ═══════════════════════════════════════════════════════════════════════════
// INSTÂNCIAS (conversa individual de cada aluno num grupo)
// Mensagens ficam numa subcollection para não estourar o limite de 1MB
// ═══════════════════════════════════════════════════════════════════════════

// Migra mensagens legadas do array do doc para a subcollection (uma vez por instância)
async function _migrateMessagesIfNeeded(docRef, docData) {
  if (!docData || docData.migrated || !Array.isArray(docData.messages) || docData.messages.length === 0) {
    return
  }
  const batch = writeBatch(db)
  const msgsCol = collection(docRef, 'messages')
  docData.messages.forEach((msg) => {
    // ID determinístico — migração é idempotente mesmo se rodar 2x em paralelo
    const id = `m_${msg.ts || 0}_${(msg.sender || 'x').replace(/\s+/g, '_')}`
    batch.set(doc(msgsCol, id), msg)
  })
  batch.update(docRef, { migrated: true, messages: deleteField() })
  await batch.commit()
}

// Busca ou cria a instância do aluno neste grupo
export async function getOrCreateInstance(groupId, userId) {
  const ref = doc(db, 'groups', groupId, 'instances', userId)
  const snap = await getDoc(ref)

  if (!snap.exists()) {
    await setDoc(ref, {
      userId,
      groupId,
      history:        [],    // contexto enviado à API (últimas 10 msgs)
      debateStarted:  false,
      lastReadCount:  0,
      migrated:       true,  // já nasce no formato novo
      createdAt:      serverTimestamp(),
      lastActive:     serverTimestamp(),
    })
    return { history: [], debateStarted: false, lastReadCount: 0 }
  }

  await _migrateMessagesIfNeeded(ref, snap.data())
  return snap.data()
}

// Escuta mensagens da instância em tempo real (estado do doc + subcollection de mensagens)
export function listenToInstance(groupId, userId, callback) {
  const ref = doc(db, 'groups', groupId, 'instances', userId)
  let docState = null
  let messages = null
  const emit = () => {
    if (docState !== null && messages !== null) {
      callback({ ...docState, messages })
    }
  }
  const unsubDoc = onSnapshot(ref, (snap) => {
    if (snap.exists()) {
      docState = snap.data()
      emit()
    }
  })
  const unsubMsgs = onSnapshot(
    query(collection(ref, 'messages'), orderBy('ts')),
    (snap) => {
      messages = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      emit()
    }
  )
  return () => { unsubDoc(); unsubMsgs() }
}

// Adiciona uma mensagem à instância e atualiza o histórico de contexto
export async function addMessageToInstance(groupId, userId, message, newHistory) {
  const ref = doc(db, 'groups', groupId, 'instances', userId)
  await addDoc(collection(ref, 'messages'), message)
  await updateDoc(ref, {
    history:    newHistory,
    lastActive: serverTimestamp(),
  })
}

// Atualiza contagem de mensagens lidas
export async function updateLastRead(groupId, userId, count) {
  const ref = doc(db, 'groups', groupId, 'instances', userId)
  await updateDoc(ref, { lastReadCount: count })
}

// Marca debate de abertura como iniciado
export async function markDebateStarted(groupId, userId) {
  const ref = doc(db, 'groups', groupId, 'instances', userId)
  await updateDoc(ref, { debateStarted: true })
}

// ═══════════════════════════════════════════════════════════════════════════
// DMs (conversa privada aluno ↔ filósofo)
// Mensagens ficam numa subcollection para não estourar o limite de 1MB
// ═══════════════════════════════════════════════════════════════════════════

// id do DM = "{userId}_{philId}" — garante unicidade e segurança pela Security Rule
function dmId(userId, philId) {
  return `${userId}_${philId}`
}

export async function getOrCreateDM(userId, philId) {
  const ref = doc(db, 'dms', dmId(userId, philId))
  const snap = await getDoc(ref)

  if (!snap.exists()) {
    await setDoc(ref, {
      userId,
      philId,
      history:       [],
      lastReadCount: 0,
      migrated:      true,
      createdAt:     serverTimestamp(),
      lastActive:    serverTimestamp(),
    })
    return { history: [], lastReadCount: 0 }
  }

  await _migrateMessagesIfNeeded(ref, snap.data())
  return snap.data()
}

export function listenToDM(userId, philId, callback) {
  const ref = doc(db, 'dms', dmId(userId, philId))
  let docState = null
  let messages = null
  const emit = () => {
    if (docState !== null && messages !== null) {
      callback({ ...docState, messages })
    }
  }
  const unsubDoc = onSnapshot(ref, (snap) => {
    if (snap.exists()) {
      docState = snap.data()
      emit()
    }
  })
  const unsubMsgs = onSnapshot(
    query(collection(ref, 'messages'), orderBy('ts')),
    (snap) => {
      messages = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      emit()
    }
  )
  return () => { unsubDoc(); unsubMsgs() }
}

// Atualiza contagem de mensagens lidas no DM
export async function updateDMLastRead(userId, philId, count) {
  const ref = doc(db, 'dms', dmId(userId, philId))
  await updateDoc(ref, { lastReadCount: count })
}

// Exclui um DM (e suas mensagens)
export async function deleteDM(userId, philId) {
  const ref = doc(db, 'dms', dmId(userId, philId))
  // Apaga primeiro as mensagens da subcollection
  const msgsSnap = await getDocs(collection(ref, 'messages'))
  const batch = writeBatch(db)
  msgsSnap.docs.forEach((d) => batch.delete(d.ref))
  batch.delete(ref)
  await batch.commit()
}

export async function addMessageToDM(userId, philId, message, newHistory) {
  const ref = doc(db, 'dms', dmId(userId, philId))
  await addDoc(collection(ref, 'messages'), message)
  await updateDoc(ref, {
    history:    newHistory,
    lastActive: serverTimestamp(),
  })
}

// Lista todos os DMs de um usuário (apenas metadados — mensagens carregam sob demanda)
export async function listDMs(userId) {
  const q = query(collection(db, 'dms'), where('userId', '==', userId))
  const snap = await getDocs(q)
  // Carrega últimas mensagens em paralelo para preview na sidebar
  return Promise.all(snap.docs.map(async (d) => {
    const data = d.data()
    // Compatibilidade: doc legado pode ainda ter messages array (até migrar)
    let messages = Array.isArray(data.messages) ? data.messages : []
    if (!messages.length) {
      const msgsSnap = await getDocs(query(collection(d.ref, 'messages'), orderBy('ts')))
      messages = msgsSnap.docs.map((m) => m.data())
    }
    return {
      id:            d.id,
      philId:        data.philId,
      messages,
      lastActive:    data.lastActive,
      lastReadCount: data.lastReadCount ?? 0,
    }
  }))
}

// ═══════════════════════════════════════════════════════════════════════════
// USUÁRIOS
// ═══════════════════════════════════════════════════════════════════════════

export async function updateUserXP(userId, xpToAdd) {
  const ref = doc(db, 'users', userId)
  await updateDoc(ref, { xp: increment(xpToAdd) })
}

export async function unlockBadge(userId, badgeId) {
  const ref = doc(db, 'users', userId)
  await updateDoc(ref, { badges: arrayUnion(badgeId) })
}

// ═══════════════════════════════════════════════════════════════════════════
// STATUSES (stories diários dos filósofos)
// ═══════════════════════════════════════════════════════════════════════════

// Busca statuses ainda válidos (não expirados)
export async function getTodayStatuses() {
  const now = Timestamp.now()
  const q = query(
    collection(db, 'statuses'),
    where('expiresAt', '>', now),
    orderBy('expiresAt'),
    orderBy('createdAt', 'desc'),
  )
  const snap = await getDocs(q)
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }))
}

// Marca status como visto pelo usuário
export async function markStatusViewed(statusId, userId) {
  const ref = doc(db, 'statuses', statusId)
  await updateDoc(ref, { views: arrayUnion(userId) })
}

// Cria um novo status (usado pela Cloud Function)
export async function createStatus({ philId, imageURL, caption }) {
  const now = new Date()
  const expiresAt = Timestamp.fromDate(new Date(now.getTime() + 24 * 60 * 60 * 1000))
  const ref = doc(collection(db, 'statuses'))
  await setDoc(ref, {
    philId,
    imageURL,
    caption,
    views:     [],
    createdAt: serverTimestamp(),
    expiresAt,
  })
  return ref.id
}

// ═══════════════════════════════════════════════════════════════════════════
// ADMIN
// ═══════════════════════════════════════════════════════════════════════════

// Busca usuário por email
export async function getUserByEmail(email) {
  const q = query(collection(db, 'users'), where('email', '==', email))
  const snap = await getDocs(q)
  if (snap.empty) return null
  const d = snap.docs[0]
  const data = d.data()
  return { uid: d.id, name: data.name, email: data.email, role: data.role, photoURL: data.photoURL }
}

// Atualiza role de um usuário
export async function updateUserRole(uid, role) {
  const ref = doc(db, 'users', uid)
  await updateDoc(ref, { role })
}
